import { useState } from 'react';
import {
  top10Categories,
  top10TypeConfig,
  top10Reglas,
  top10GlobalStats,
  top10Radios,
  top10Programas,
  top10Locutores,
  top10Clubes,
  top10Fans,
  top10Eventos,
  top10Concursos,
  formatearNumero,
  type Top10EntityType,
  type Top10Entry,
} from '@/mocks/top10Global';

const movementColors: Record<string, { icon: string; color: string; bg: string }> = {
  up: { icon: 'ri-arrow-up-line', color: 'text-emerald-500', bg: 'bg-emerald-50' },
  down: { icon: 'ri-arrow-down-line', color: 'text-red-500', bg: 'bg-red-50' },
  stable: { icon: 'ri-subtract-line', color: 'text-foreground-400', bg: 'bg-foreground-100' },
};

const trendColors: Record<string, { bg: string; dot: string }> = {
  exploding: { bg: 'bg-rose-500', dot: 'bg-rose-400' },
  trending: { bg: 'bg-amber-500', dot: 'bg-amber-400' },
  growing: { bg: 'bg-emerald-500', dot: 'bg-emerald-400' },
  active: { bg: 'bg-sky-500', dot: 'bg-sky-400' },
  stable: { bg: 'bg-foreground-400', dot: 'bg-foreground-300' },
  declining: { bg: 'bg-red-500', dot: 'bg-red-400' },
};

function Top10Card({ entry, rank, typeColor }: { entry: Top10Entry; rank: number; typeColor: string }) {
  const movement = movementColors[entry.movement];

  return (
    <div className="flex items-center gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-background-300/60 transition-all cursor-pointer group">
      {/* position */}
      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center">
        <span className={`text-sm font-bold ${rank <= 3 ? typeColor : 'text-foreground-600'}`}>#{entry.position}</span>
      </div>

      {/* movement */}
      <div className={`flex-shrink-0 w-6 h-6 rounded-md ${movement.bg} flex items-center justify-center`}>
        <i className={`${movement.icon} ${movement.color} text-xs`}></i>
      </div>

      {/* logo */}
      <img src={entry.logoUrl} alt={entry.entityName} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" loading="lazy" />

      {/* info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-bold text-foreground-900 truncate">{entry.entityName}</p>
          {entry.isLive && (
            <span className="flex-shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 text-[9px] font-bold">
              <span className="w-1 h-1 rounded-full bg-red-500 animate-pulse"></span>
              EN VIVO
            </span>
          )}
        </div>
        <p className="text-xs text-foreground-500 truncate">{entry.entitySubtitle}</p>
      </div>

      {/* score */}
      <div className="flex-shrink-0 text-right">
        <p className="text-sm font-bold text-foreground-900">{entry.scoreLabel}</p>
        <div className="flex items-center justify-end gap-1 mt-0.5">
          <span className={`w-1.5 h-1.5 rounded-full ${trendColors[entry.trend]?.dot}`}></span>
          <span className={`text-xs font-semibold ${entry.scoreChange >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
            {entry.scoreChange >= 0 ? '+' : ''}{entry.scoreChange}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function Top10GlobalSection() {
  const [activeTab, setActiveTab] = useState<Top10EntityType>('radios');
  const [showReglas, setShowReglas] = useState(false);

  const currentEntries = top10Categories[activeTab];
  const typeInfo = top10TypeConfig[activeTab];

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-7xl mx-auto">
        {/* header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 mb-4">
            <i className="ri-trophy-line text-amber-600 text-sm"></i>
            <span className="text-xs font-bold text-amber-700 whitespace-nowrap">COMPETENCIA VISIBLE</span>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Top 10 Global
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
            El ranking que mide competencia real en 7 categorías. Actualización automática cada 60 segundos. Posición, movimiento, score y tendencia — todo visible.
          </p>

          {/* mini stats */}
          <div className="flex flex-wrap items-center justify-center gap-6 mt-6">
            <div className="text-center">
              <p className="text-lg font-bold text-foreground-900">{top10GlobalStats.totalEntidadesRankeadas}</p>
              <p className="text-xs text-foreground-500">Entidades</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-foreground-900">7</p>
              <p className="text-xs text-foreground-500">Categorías</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                <p className="text-lg font-bold text-foreground-900">{top10GlobalStats.actualizacionesPorHora}</p>
              </div>
              <p className="text-xs text-foreground-500">Actualizaciones/h</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-foreground-900">{top10GlobalStats.entidadesEnVivoAhora}</p>
              <p className="text-xs text-foreground-500">En Vivo</p>
            </div>
          </div>
        </div>

        {/* tab bar + toggle */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-8">
          <div className="flex flex-wrap items-center gap-1.5">
            {(Object.keys(top10Categories) as Top10EntityType[]).map((type) => {
              const info = top10TypeConfig[type];
              const count = top10Categories[type].length;
              return (
                <button
                  key={type}
                  onClick={() => { setActiveTab(type); setShowReglas(false); }}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                    activeTab === type && !showReglas
                      ? `${info.bg} ${info.color}`
                      : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  <i className={`${info.icon} text-xs`}></i>
                  {info.label}
                  <span className="text-[10px] opacity-60">({count})</span>
                </button>
              );
            })}

            {/* reglas toggle */}
            <button
              onClick={() => setShowReglas(!showReglas)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                showReglas
                  ? 'bg-violet-50 text-violet-600'
                  : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
              }`}
            >
              <i className="ri-scales-line text-xs"></i>
              Reglas
            </button>
          </div>

          {/* live indicator */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-emerald-50 border border-emerald-200">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            <span className="text-xs font-semibold text-emerald-700 whitespace-nowrap">Actualización automática</span>
          </div>
        </div>

        {/* content */}
        {!showReglas ? (
          <>
            {/* type description */}
            <div className="mb-5 p-4 rounded-xl bg-background-100 border border-background-200/70">
              <div className="flex items-center gap-2 mb-1">
                <i className={`${typeInfo.icon} ${typeInfo.color} text-sm`}></i>
                <span className="text-sm font-semibold text-foreground-800">{typeInfo.label}</span>
              </div>
              <p className="text-xs text-foreground-600">{typeInfo.description}</p>
            </div>

            {/* top 3 featured */}
            {currentEntries.length >= 3 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {currentEntries.slice(0, 3).map((entry, idx) => {
                  const movement = movementColors[entry.movement];
                  const typeCfg = top10TypeConfig[activeTab];
                  return (
                    <div
                      key={entry.entityName}
                      className={`relative p-5 rounded-2xl border ${
                        idx === 0 ? 'bg-amber-50 border-amber-200' : idx === 1 ? 'bg-slate-50 border-slate-200' : 'bg-orange-50 border-orange-200'
                      } transition-all hover:shadow-sm cursor-pointer`}
                    >
                      {/* podium badge */}
                      <div className="absolute top-3 right-3">
                        <span className={`text-2xl font-black ${
                          idx === 0 ? 'text-amber-400' : idx === 1 ? 'text-slate-400' : 'text-orange-400'
                        }`}>
                          #{entry.position}
                        </span>
                      </div>

                      <div className="flex items-center gap-3 mb-3">
                        <img src={entry.logoUrl} alt="" className="w-12 h-12 rounded-xl object-cover" loading="lazy" />
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-foreground-900 truncate">{entry.entityName}</p>
                          <p className="text-xs text-foreground-500 truncate">{entry.entitySubtitle.split('·')[0]}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1.5">
                          <div className={`w-6 h-6 rounded-md ${movement.bg} flex items-center justify-center`}>
                            <i className={`${movement.icon} ${movement.color} text-xs`}></i>
                          </div>
                          <span className="text-xs font-semibold text-foreground-700">{entry.movementValue}</span>
                        </div>
                        <span className={`text-sm font-bold ${typeCfg.color}`}>{entry.scoreLabel}</span>
                        <span className={`text-xs font-semibold ${entry.scoreChange >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                          {entry.scoreChange >= 0 ? '+' : ''}{entry.scoreChange}
                        </span>
                        {entry.isLive && (
                          <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 text-[9px] font-bold ml-auto">
                            <span className="w-1 h-1 rounded-full bg-red-500 animate-pulse"></span>
                            EN VIVO
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* list 4-10 */}
            <div className="space-y-2">
              {currentEntries.slice(3, 10).map((entry) => (
                <Top10Card key={entry.entityName} entry={entry} rank={entry.position} typeColor={typeInfo.color} />
              ))}
            </div>

            {/* CTA */}
            <div className="mt-8 text-center">
              <a
                href="/top-10-global"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-bold hover:from-amber-600 hover:to-orange-600 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-trophy-line"></i>
                Ver Top 10 Global Completo
              </a>
            </div>
          </>
        ) : (
          /* reglas view */
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {top10Reglas.map((regla) => (
              <div key={regla.id} className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-r ${regla.gradiente} flex items-center justify-center flex-shrink-0`}>
                    <i className={`${regla.icono} text-white text-lg`}></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-foreground-900 mb-2">{regla.titulo}</h4>
                    <p className="text-xs text-foreground-600 leading-relaxed">{regla.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}